// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/AuthContext';
import useNotificationSystem from './hooks/useNotificationSystem';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import useOnlineStatus from './hooks/useOnlineStatus';
import { I18nProvider } from './contexts/I18nContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { getWorkspace } from './services/workspaceService';
import InstallPrompt from './components/ui/InstallPrompt';
import GlobalChat from './components/ui/GlobalChat';
import VideoCall from './components/ui/VideoCall';
import usePushNotifications from './hooks/usePushNotifications';
import { initAppConfig } from './services/appConfig';

// Pages
import Login from './components/pages/Login';
import RegisterBusiness from './components/pages/RegisterBusiness';
import OnboardingWizard from './components/pages/OnboardingWizard';
import Panel from './components/pages/Panel';
import Home from './components/pages/Home';
import Inbox from './components/pages/Inbox';
import AdminSettings from './components/pages/AdminSettings';
import SuperAdminDashboard from './components/pages/SuperAdminDashboard';
import WorkspacePlan from './components/pages/WorkspacePlan';
import WorkspaceBranding from './components/pages/WorkspaceBranding';
import PendingApproval from './components/pages/PendingApproval';
import CategoriesGallery from './components/pages/CategoriesGallery';
import SectionsManager from './components/pages/SectionsManager';
import SalesAdvisorPage from './components/pages/SalesAdvisorPage';
import SinAcceso from './components/pages/SinAcceso';
import PublicSchedulePage from './modules/calendar/pages/PublicSchedulePage';
import InstructorCalendarView from './modules/calendar/pages/InstructorCalendarView';
import InstructorSettings from './components/pages/InstructorSettings';
import CalendarPage from './modules/calendar/pages/CalendarPage';
import InstructorsPage from './modules/calendar/pages/InstructorsPage';
import CoursesPage from './modules/calendar/pages/CoursesPage';
import DashboardPage from './modules/calendar/pages/DashboardPage';
import PlanningPage from './modules/calendar/pages/PlanningPage';
import AsesoresPage from './modules/calendar/pages/AsesoresPage';
import CalendarSettingsPage from './modules/calendar/pages/CalendarSettingsPage';
import ZoomAccountsPage from './modules/calendar/pages/ZoomAccountsPage';
import { QuickMessages, Automations, MediaLibrary, Statistics, Settings } from './components/pages/OtherPages';

// Layout
import MainLayout from './components/layout/MainLayout';

// UI Components
// La barra superior (components/ui/RightSidebar.jsx) se reemplazó por AppSidebar;
// se conserva el archivo por si hay que volver atrás.
import AppSidebar from './components/layout/AppSidebar';
import HomeDashboard from './components/pages/HomeDashboard';

// Modules
import MessageGeneratorApp from './modules/messageGenerator/MessageGeneratorApp';
import TaskManagerApp from './modules/taskManager/TaskManagerApp';
import OvertimeCalculator from './components/pages/OvertimeCalculator';
import AIChat from './components/pages/AIChat';
import CalendarLayout from './modules/calendar/layouts/CalendarLayout';
import PublicFormPage from './modules/taskManager/pages/PublicFormPage';
import SupabaseTest from './components/pages/SupabaseTest';

import './App.css';
import './styles/hx-shell.css';
import './styles/hx-pages.css';

// Gestor Pro (base de respuestas), Agente IA y Generador (con su Seguimiento
// diario) quedaron obsoletos: ahora hay bot y los cursos viven en Calendario.
// Con true sus rutas redirigen al Inicio y no aparecen en ningún menú.
// Cuentas Zoom sigue disponible en Calendario (/zoom-accounts).
const MODULOS_OCULTOS = true;

// ─────────────────────────────────────────────────────────────────────────────
// PermRoute: Protege una ruta verificando appPermissions en el doc del usuario.
// Admin (role === 'admin') siempre tiene acceso.
// Si permKey === null → solo requiere estar autenticado (sin permiso específico).
// ─────────────────────────────────────────────────────────────────────────────
const PermRoute = ({ permKey, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return children;
  if (permKey === null) return children; // ruta pública autenticada
  const perms = user?.appPermissions || {};
  // Si el permiso es explícitamente false → denegar
  if (perms[permKey] === false) return <Navigate to="/sin-acceso" replace />;
  return children;
};

// Helper: primer módulo permitido para el redirect inicial
const getDefaultPath = (user) => {
  if (!user) return '/login';
  // Cualquier instructor (no admin) va directo a su calendario.
  if (user.isInstructor && user.role !== 'admin') return '/mi-calendario';
  // Todos los demás entran al Inicio (dashboard con accesos y resumen del día).
  // Gestor Pro y Agente IA quedaron ocultos: ya no son destino por defecto.
  return '/inicio';
};


// Componente interno para manejar las apps
function AppContent() {
  const { user, loading } = useAuth();
  useNotificationSystem();
  usePushNotifications(user);
  const isOnline = useOnlineStatus();
  const [needsOnboarding, setNeedsOnboarding] = React.useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = React.useState(false);

  // Verificar si el usuario necesita onboarding (es admin de workspace y no completó onboarding)
  React.useEffect(() => {
    const checkOnboarding = async () => {
      if (!user || user.status !== 'active' || user.isCollaborator) return;
      if (user.email === 'vcecursos@gmail.com') return; // super-admin no necesita onboarding
      setCheckingOnboarding(true);
      try {
        const ws = await getWorkspace(user.workspaceId || user.uid);
        if (ws && !ws.onboardingCompleted) {
          setNeedsOnboarding(true);
        }
      } catch { /* silently fail */ }
      setCheckingOnboarding(false);
    };
    checkOnboarding();
  }, [user]);

  // Inicializar el listener global de app_config/gemini cuando hay sesión.
  // El rule de Firestore exige auth, por eso esperamos a tener user.
  React.useEffect(() => {
    if (user) initAppConfig();
  }, [user]);

  if (loading) {
    return (
      <div className="h-screen bg-white dark:bg-[#13161c]">
        <div className="fixed top-0 left-0 w-full h-1 z-50 overflow-hidden bg-gray-200/50 dark:bg-gray-800/50">
          <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-loading-bar" />
        </div>
      </div>
    );
  }

  // Si no está autenticado, solo mostrar login/registro (pero permitir formularios públicos)
  if (!user) {
    return (
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/formulario/:formId" element={<PublicFormPage />} />
          <Route path="/horario/:instructorId" element={<PublicSchedulePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<RegisterBusiness />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Si está pendiente o rechazado, mostrar página de aprobación
  if (user.status === 'pending' || user.status === 'rejected') {
    return (
      <BrowserRouter>
        <Toaster position="top-right" />
        <Routes>
          <Route path="*" element={<PendingApproval />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Si el usuario necesita completar onboarding
  if (user.status === 'active' && needsOnboarding && !checkingOnboarding) {
    return (
      <BrowserRouter>
        <Toaster position="top-right" />
        <OnboardingWizard onComplete={() => setNeedsOnboarding(false)} />
      </BrowserRouter>
    );
  }

  // Si el usuario está activo, mostrar las apps
  if (user.status === 'active' && !checkingOnboarding) {
    const defaultPath = getDefaultPath(user);
    return (
      <BrowserRouter>
        <Toaster position="top-right" />
        {!isOnline && (
          <div className="bg-amber-500 text-white text-center py-1.5 text-xs font-medium z-50 relative">
            Sin conexion - Modo offline. Los cambios se sincronizaran al reconectar.
          </div>
        )}
        <div className="hxa flex flex-col lg:flex-row h-screen bg-white dark:bg-gray-900">
          {/* Menú lateral (estilo beta.hingenia.com) */}
          <AppSidebar />

          {/* Contenido principal */}
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            <Routes>
              {/* Inicio: accesos rápidos + resumen del día */}
              <Route path="/inicio" element={<HomeDashboard />} />
              <Route path="/" element={<Navigate to={defaultPath} replace />} />

              {/* Páginas públicas (accesibles auth y no-auth) */}
              <Route path="/formulario/:formId" element={<PublicFormPage />} />
              <Route path="/horario/:instructorId" element={<PublicSchedulePage />} />

              {/* Página de acceso denegado — un instructor nunca queda atrapado aquí */}
              <Route path="/sin-acceso" element={
                (user.isInstructor && user.role !== 'admin')
                  ? <Navigate to="/mi-calendario" replace />
                  : <SinAcceso />
              } />

              {/* Ruta del Generador de Mensajes (desactivado, ver MODULOS_OCULTOS) */}
              {!MODULOS_OCULTOS && (
                <Route path="/generador/*" element={<PermRoute permKey="generador"><MessageGeneratorApp /></PermRoute>} />
              )}

              {/* Ruta del Task Manager */}
              <Route path="/tareas/*" element={<PermRoute permKey="tareas"><TaskManagerApp /></PermRoute>} />

              {/* Ruta Calculadora Horas Extras */}
              <Route path="/horas-extras" element={<PermRoute permKey="horasExtra"><OvertimeCalculator /></PermRoute>} />

              {/* Gestor Pro y Agente IA DESACTIVADOS: cualquier URL suya (también las
                  guardadas en favoritos) redirige al Inicio. El código se conserva;
                  para reactivarlos poner MODULOS_OCULTOS = false. */}
              {MODULOS_OCULTOS && [
                '/ia', '/panel', '/categories', '/category/:categoryId', '/sections',
                '/inbox', '/quick-messages', '/automations', '/media-library', '/generador/*',
              ].map((p) => <Route key={p} path={p} element={<Navigate to={defaultPath} replace />} />)}

              {/* Super Agente IA */}
              {!MODULOS_OCULTOS && (
                <Route path="/ia" element={<PermRoute permKey="agenteIA"><AIChat /></PermRoute>} />
              )}

              {/* Rutas del Gestor Pro — un instructor (sin acceso al Gestor) se manda
                  directo a su calendario en vez de bloquearlo en /sin-acceso */}
              {!MODULOS_OCULTOS && (
              <Route path="/" element={
                (user.isInstructor && user.role !== 'admin')
                  ? <Navigate to="/mi-calendario" replace />
                  : <PermRoute permKey="gestorPro"><MainLayout /></PermRoute>
              }>
                <Route path="panel" element={<Panel />} />
                <Route path="categories" element={<CategoriesGallery />} />
                <Route path="category/:categoryId" element={<Home />} />
                <Route path="sections" element={<SectionsManager />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="quick-messages" element={<QuickMessages />} />
                <Route path="automations" element={<Automations />} />
                <Route path="media-library" element={<MediaLibrary />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              )}

              {/* CALENDARIO ACADÉMICO */}
              <Route path="/" element={<PermRoute permKey="calendario"><CalendarLayout /></PermRoute>}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="planning" element={<PlanningPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="instructors" element={<InstructorsPage />} />
                <Route path="courses" element={<CoursesPage />} />
                <Route path="zoom-accounts" element={<ZoomAccountsPage />} />
                <Route path="asesores" element={<AsesoresPage />} />
                <Route path="calendar-settings" element={<CalendarSettingsPage />} />
              </Route>

              {/* Ruta Módulo de Ventas Asesores */}
              <Route path="/sales-advisor" element={
                <PermRoute permKey="ventas">
                  {(user.isSeller || user.role === 'admin') ? <SalesAdvisorPage /> : <Navigate to="/sin-acceso" replace />}
                </PermRoute>
              } />

              {/* Settings - instructor gets simplified version */}
              <Route path="/settings" element={
                user.isInstructor && !user.isCollaborator && user.role !== 'admin'
                  ? <InstructorSettings />
                  : <Settings />
              } />

              {/* Supabase Test - temporal */}
              <Route path="/supabase-test" element={<SupabaseTest />} />

              {/* Plan y personalizacion del workspace */}
              <Route path="/mi-plan" element={<WorkspacePlan />} />
              <Route path="/mi-marca" element={<WorkspaceBranding />} />

              {/* Admin Panel - Solo para admins */}
              {user.role === 'admin' && (
                <Route path="/admin" element={<AdminSettings />} />
              )}
              {/* Super Admin Dashboard */}
              {user.email === 'vcecursos@gmail.com' && (
                <Route path="/super-admin" element={<SuperAdminDashboard />} />
              )}

              {/* Instructor Dashboard — instructores ven el suyo; admin puede ver el de cualquiera */}
              {(user.isInstructor || user.role === 'admin' || user.appPermissions?.calInstructor === true) && (
                <Route path="/mi-calendario" element={<InstructorCalendarView />} />
              )}

              <Route path="*" element={<Navigate to={defaultPath} replace />} />
            </Routes>
          </div>

        </div>
        <GlobalChat />
        <VideoCall />
        <InstallPrompt />
      </BrowserRouter>
    );
  }

  // Fallback
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Componente principal con providers
function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <NotificationProvider>
          <BrandingProvider>
            <AppContent />
          </BrandingProvider>
        </NotificationProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
